import React, { useCallback, useRef, useState } from 'react';
import Select, { StylesConfig, SingleValue, MultiValue, GroupBase } from 'react-select';

export interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label?: string;
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  error?: string;
  variant?: 'default' | 'glass' | 'slate';
  className?: string;
  isMulti?: false;
}

interface SearchableMultiSelectProps {
  label?: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  error?: string;
  variant?: 'default' | 'glass' | 'slate';
  className?: string;
  isMulti: true;
}

type Props = SearchableSelectProps | SearchableMultiSelectProps;

const getCustomStyles = (
  hasError: boolean,
  variant: 'default' | 'glass' | 'slate'
): StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> => ({
  control: (base, state) => ({
    ...base,
    minHeight: variant === 'slate' ? '38px' : '42px',
    backgroundColor:
      variant === 'glass'
        ? 'rgba(255, 255, 255, 0.1)'
        : variant === 'slate'
          ? 'rgb(248, 250, 252)' // slate-50, igual que los filtros nativos de la tabla
          : 'var(--color-surface, #ffffff)',
    borderColor: hasError
      ? 'rgb(252, 165, 165)'
      : state.isFocused
        ? variant === 'slate'
          ? 'rgb(203, 213, 225)' // slate-300, sin glow violeta en filtros compactos
          : 'rgb(168, 85, 247)' // primary-500 (morado/violeta, ver apps/web/index.css)
        : variant === 'slate'
          ? 'rgb(226, 232, 240)' // slate-200, como los selects de filtros
          : 'rgb(203, 213, 225)', // slate-300, igual que el resto de los inputs
    borderRadius: '0.5rem',
    boxShadow: state.isFocused
      ? hasError
        ? '0 0 0 2px rgba(239, 68, 68, 0.2)'
        : variant === 'slate'
          ? '0 0 0 2px rgba(99, 102, 241, 0.12)' // focus sutil tipo primary-100
          : '0 0 0 2px rgba(168, 85, 247, 0.2)' // primary-100-ish glow
      : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '&:hover': {
      borderColor: hasError ? 'rgb(252, 165, 165)' : 'rgb(148, 163, 184)', // slate-400
    },
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 12px',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--color-text, #111827)',
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    fontSize: '0.875rem',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--color-text, #111827)',
    fontSize: '0.875rem',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgb(243, 232, 255)', // primary-100
    borderRadius: '0.375rem',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'rgb(107, 33, 168)', // primary-800
    fontSize: '0.75rem',
    padding: '2px 6px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'rgb(107, 33, 168)', // primary-800
    '&:hover': {
      backgroundColor: 'rgb(233, 213, 255)', // primary-200
      color: 'rgb(88, 28, 135)', // primary-900
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--color-surface, #ffffff)',
    border: '1px solid var(--color-border, #e5e7eb)',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 9999,
    overflow: 'hidden',
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
    maxHeight: '200px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgb(147, 51, 234)' // primary-600, igual que los botones primary del sistema
      : state.isFocused
        ? 'rgb(241, 245, 249)' // slate-100
        : 'transparent',
    color: state.isSelected ? '#ffffff' : 'var(--color-text, #111827)',
    fontSize: '0.875rem',
    padding: '10px 12px',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? 'rgb(147, 51, 234)' : 'rgb(226, 232, 240)', // slate-200
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    fontSize: '0.875rem',
    padding: '10px 12px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    padding: '8px',
    transition: 'transform 0.2s',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
    '&:hover': {
      color: 'var(--color-text-secondary, #6b7280)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    padding: '8px',
    '&:hover': {
      color: 'rgb(239, 68, 68)',
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
});

const MENU_MAX_HEIGHT = 280;
/** Un contenedor más bajo que esto no se toma como límite (wrappers sueltos). */
const MIN_BOUNDARY_HEIGHT = 160;

/**
 * Primer ancestro que recorta o scrollea, que es el borde visual real del
 * menú: la tarjeta de un modal, un panel con scroll, etc.
 */
const findBoundary = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null;
  while (node && node !== document.body) {
    const { overflowY, overflow } = window.getComputedStyle(node);
    const clips = [overflowY, overflow].some((v) =>
      ['auto', 'scroll', 'hidden'].includes(v),
    );
    if (clips && node.getBoundingClientRect().height >= MIN_BOUNDARY_HEIGHT) return node;
    node = node.parentElement;
  }
  return null;
};

export const SearchableSelect: React.FC<Props> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  isSearchable = true,
  isClearable = false,
  isDisabled = false,
  error,
  variant = 'default',
  className = '',
  isMulti,
}) => {
  const customStyles = getCustomStyles(!!error, variant);
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = useState<'auto' | 'top'>('auto');

  /**
   * `menuPlacement="auto"` mide contra el VIEWPORT, no contra el contenedor.
   * En un modal eso abre el menú hacia abajo siempre que quede sitio en la
   * pantalla, aunque se salga de la tarjeta: el menú acababa pintado sobre el
   * fondo y tapando los botones. Aquí se mide contra el contenedor real.
   */
  const resolveMenuPlacement = useCallback(() => {
    const boundary = findBoundary(containerRef.current);
    if (!boundary || !containerRef.current) {
      setMenuPlacement('auto');
      return;
    }
    const field = containerRef.current.getBoundingClientRect();
    const box = boundary.getBoundingClientRect();
    const below = box.bottom - field.bottom;
    const above = field.top - box.top;
    setMenuPlacement(below < MENU_MAX_HEIGHT && above > below ? 'top' : 'auto');
  }, []);

  const handleChange = (
    newValue: SingleValue<SelectOption> | MultiValue<SelectOption>
  ) => {
    if (isMulti) {
      const multiOnChange = onChange as (value: string[]) => void;
      const multiValue = newValue as MultiValue<SelectOption>;
      multiOnChange(multiValue ? multiValue.map(v => v.value) : []);
    } else {
      const singleOnChange = onChange as (value: string | null) => void;
      const singleValue = newValue as SingleValue<SelectOption>;
      singleOnChange(singleValue ? singleValue.value : null);
    }
  };

  const selectedValue = isMulti
    ? options.filter(opt => (value as string[]).includes(opt.value))
    : options.find(opt => opt.value === value) || null;

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      <Select<SelectOption, boolean, GroupBase<SelectOption>>
        options={options}
        value={selectedValue}
        onChange={handleChange}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        styles={customStyles}
        noOptionsMessage={() => 'Sin opciones'}
        loadingMessage={() => 'Cargando...'}
        menuPlacement={menuPlacement}
        maxMenuHeight={MENU_MAX_HEIGHT}
        onMenuOpen={resolveMenuPlacement}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        classNamePrefix="react-select"
      />
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default SearchableSelect;
